/**
 * @typedef {Object} AppConfig
 * @property {string} baseUrl Backend origin without the `/api` prefix.
 * @property {string} apiUrl Backend REST API origin including the `/api` prefix.
 * @property {string} socketUrl Realtime server origin.
 */

export function normalizeUrl(value) {
    return String(value).trim().replace(/\/+$/, "");
}

export function requireEnv(name, value) {
    const normalizedValue = normalizeUrl(value || "");

    if (!normalizedValue) {
        throw new Error(`Missing required environment variable: ${name}`);
    }

    return normalizedValue;
}

/**
 * Builds the frontend URL contract from Create React App build-time variables.
 *
 * @param {NodeJS.ProcessEnv|Record<string, string|undefined>} env
 * @returns {Readonly<AppConfig>}
 */
export function createAppConfig(env = process.env) {
    const isDevelopment = env.NODE_ENV === "development";
    const prefix = isDevelopment ? "REACT_APP_DEV_" : "REACT_APP_";

    return Object.freeze({
        baseUrl: requireEnv(`${prefix}BASE_URL`, env[`${prefix}BASE_URL`]),
        apiUrl: requireEnv(`${prefix}API_URL`, env[`${prefix}API_URL`]),
        socketUrl: requireEnv(`${prefix}SOCKET_URL`, env[`${prefix}SOCKET_URL`]),
    });
}

export const appConfig = createAppConfig();

/**
 * Keeps absolute backend/media URLs unchanged and resolves legacy relative URLs
 * against the backend root (never against the `/api` URL).
 *
 * @param {string|null|undefined} value
 * @param {string} baseUrl
 * @returns {string|null}
 */
export function resolveUrl(value, baseUrl = appConfig.baseUrl) {
    if (!value) return null;

    const normalizedValue = String(value).trim();
    if (!normalizedValue) return null;

    if (/^https?:\/\//i.test(normalizedValue)) {
        return normalizedValue;
    }

    return `${normalizeUrl(baseUrl)}/${normalizedValue.replace(/^\/+/, "")}`;
}
